import {
  LOAN_LIST_REQUEST,
  LOAN_LIST_REQUEST_ERROR,
  LOAN_LIST_REQUEST_SUCCESS,
  LOAN_REQUEST,
  LOAN_REQUEST_ERROR,
  LOAN_REQUEST_SUCCESS,
  LOAN_REPAYMENT_REQUEST,
  LOAN_REPAYMENT_REQUEST_ERROR,
  LOAN_REPAYMENT_REQUEST_SUCCESS,
  LENDING_REQUEST,
  LENDING_REQUEST_ERROR,
  LENDING_REQUEST_SUCCESS,
  SEND_SMS_REQUEST,
  SEND_SMS_REQUEST_ERROR,
  SEND_SMS_REQUEST_SUCCESS,
  DOWNLOAD_LOAN_NOTICE_REQUEST,
  DOWNLOAD_LOAN_NOTICE_REQUEST_ERROR,
  DOWNLOAD_LOAN_NOTICE_REQUEST_SUCCESS,
} from '@/store/actions/loan';
import { handleRequestError } from '@/utils/error';

import Repository, { LOANS } from '@/repositories/RepositoryFactory';

const LoansRepository = Repository.get(LOANS);

const state = {
  loan: {},
  loanList: [],
  loanMeta: '',
  loanStatus: '',
  lendingStatus: '',
  lendingId: null,
  sendSMSStatus: '',
  sendSMSType: '',
  downloadLoanNoticeLink: '',
  downloadLoanNoticeStatus: '',
};

const getters = {
  loan: state => state.loan,
  loanList: state => state.loanList,
  loanMeta: state => state.loanMeta,
  loanStatus: state => state.loanStatus,
  lendingStatus: state => state.lendingStatus,
  lendingId: state => state.lendingId,
  sendSMSStatus: state => state.sendSMSStatus,
  sendSMSType: state => state.sendSMSType,
  downloadLoanNoticeLink: state => state.downloadLoanNoticeLink,
  downloadLoanNoticeStatus: state => state.downloadLoanNoticeStatus,
};

const actions = {
  [LOAN_LIST_REQUEST]: async ({ commit }, payload) => {
    commit(LOAN_LIST_REQUEST);
    try {
      const response = await LoansRepository.get(payload);
      commit(LOAN_LIST_REQUEST_SUCCESS, response.data);
    } catch (error) {
      commit(LOAN_LIST_REQUEST_ERROR);
      handleRequestError(error);
    }
  },
  [LOAN_REQUEST]: async ({ commit }, payload) => {
    commit(LOAN_REQUEST);
    try {
      const response = await LoansRepository.show(payload);
      const loan = response.data.data;
      commit(LOAN_REQUEST_SUCCESS, loan);
    } catch (error) {
      commit(LOAN_REQUEST_ERROR);
      handleRequestError(error);
    }
  },
  [LOAN_REPAYMENT_REQUEST]: async ({ commit }, payload) => {
    commit(LOAN_REPAYMENT_REQUEST);
    try {
      const { type, id, data } = payload;
      let response = null;
      if (type === 'extend') {
        response = await LoansRepository.extend(id, data);
      } else if (type === 'writeOff') {
        response = await LoansRepository.writeOff(id, data);
      } else {
        response = await LoansRepository.pay(id, data);
      }
      commit(LOAN_REPAYMENT_REQUEST_SUCCESS, response.data.data);
    } catch (error) {
      commit(LOAN_REPAYMENT_REQUEST_ERROR);
      handleRequestError(error);
    }
  },
  [LENDING_REQUEST]: async ({ commit }, id) => {
    commit(LENDING_REQUEST, id);
    try {
      await LoansRepository.lending(id);
      commit(LENDING_REQUEST_SUCCESS);
    } catch (error) {
      commit(LENDING_REQUEST_ERROR);
      handleRequestError(error);
    }
  },
  [SEND_SMS_REQUEST]: async ({ commit }, payload) => {
    const { id, data } = payload;
    commit(SEND_SMS_REQUEST, data.type || '');
    try {
      await LoansRepository.sendSMS(id, data);
      commit(SEND_SMS_REQUEST_SUCCESS);
    } catch (error) {
      commit(SEND_SMS_REQUEST_ERROR);
      handleRequestError(error);
    }
  },
  [DOWNLOAD_LOAN_NOTICE_REQUEST]: async ({ commit }, id) => {
    commit(DOWNLOAD_LOAN_NOTICE_REQUEST);
    try {
      const response = await LoansRepository.downloadNotice(id);
      commit(DOWNLOAD_LOAN_NOTICE_REQUEST_SUCCESS, response.data.data);
    } catch (error) {
      commit(DOWNLOAD_LOAN_NOTICE_REQUEST_ERROR);
      handleRequestError(error);
    }
  },
};

const mutations = {
  [LOAN_LIST_REQUEST]: state => {
    state.loanStatus = 'loading';
  },
  [LOAN_LIST_REQUEST_SUCCESS]: (state, payload) => {
    state.loanStatus = 'success';
    state.loanList = payload.data;
    state.loanMeta = payload.meta;
  },
  [LOAN_LIST_REQUEST_ERROR]: state => {
    state.loanStatus = 'error';
  },
  [LOAN_REQUEST]: state => {
    state.loanStatus = 'loading';
  },
  [LOAN_REQUEST_SUCCESS]: (state, payload) => {
    state.loanStatus = 'success';
    state.loan = payload;
  },
  [LOAN_REQUEST_ERROR]: state => {
    state.loanStatus = 'error';
  },
  [LOAN_REPAYMENT_REQUEST]: state => {
    state.loanStatus = 'repaying';
  },
  [LOAN_REPAYMENT_REQUEST_SUCCESS]: (state, payload) => {
    state.loanStatus = 'success';
    const { status, paidAmount } = payload;
    state.loan = {
      ...state.loan,
      status,
      paidAmount,
    };
  },
  [LOAN_REPAYMENT_REQUEST_ERROR]: state => {
    state.loanStatus = 'error';
  },
  [LENDING_REQUEST]: (state, payload) => {
    state.lendingStatus = 'loading';
    state.lendingId = payload;
  },
  [LENDING_REQUEST_SUCCESS]: state => {
    state.lendingStatus = 'success';
    state.lendingId = null;
  },
  [LENDING_REQUEST_ERROR]: state => {
    state.lendingStatus = 'error';
    state.lendingId = null;
  },
  [SEND_SMS_REQUEST]: (state, payload) => {
    state.sendSMSStatus = 'loading';
    state.sendSMSType = payload;
  },
  [SEND_SMS_REQUEST_SUCCESS]: state => {
    state.sendSMSStatus = 'success';
  },
  [SEND_SMS_REQUEST_ERROR]: state => {
    state.sendSMSStatus = 'error';
  },
  [DOWNLOAD_LOAN_NOTICE_REQUEST]: state => {
    state.downloadLoanNoticeStatus = 'loading';
  },
  [DOWNLOAD_LOAN_NOTICE_REQUEST_SUCCESS]: (state, payload) => {
    state.downloadLoanNoticeStatus = 'success';
    state.downloadLoanNoticeLink = payload;
  },
  [DOWNLOAD_LOAN_NOTICE_REQUEST_ERROR]: state => {
    state.downloadLoanNoticeStatus = 'error';
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
